import React, { useState, useEffect } from 'react';
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Label } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, LockClosedIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { classNames } from 'utils/styleUtils';

/**
 * Predefined Program options for the AstraZeneca Flow.
 */
export const PROGRAM_OPTIONS: string[] = [
  'Autonomous Manufacturing',
  'Self Healing Supply Chain',
  'Digital Supply Network Collaboration',
  'Sustainability',
  'AIDA',
  'BAU',
];

interface ProgramMultiSelectProps {
  /** Comma-separated string value from Redux (e.g. "BAU, Sustainability"). */
  value: string;
  /** Called with the new comma-separated string whenever the selection changes. */
  onChange: (value: string) => void;
  /**
   * Programs that were auto-filled from the Use Case → Program mapping and are therefore
   * non-editable by the user. Passed down from DataProductDefinitionStep (task 3.5).
   * These are rendered as locked chips with no remove button and a slightly different
   * background to signal they are read-only (task 4.2).
   */
  lockedValues?: string[];
}

/**
 * Multi-select combobox for the Program field.
 *
 * - Accepts/emits a comma-separated string to stay compatible with the Redux
 *   `program: string` state and the backend `program` field.
 * - Supports selecting multiple predefined options.
 * - Only predefined values from PROGRAM_OPTIONS can be selected; free-text
 *   custom values are not permitted.
 * - Typing is allowed for search/filter purposes only.
 * - Values listed in `lockedValues` (auto-filled from mapping) are rendered as
 *   non-removable chips; only unmapped programs (BAU defaults) can be removed
 *   or changed by the user (tasks 4.2, 4.3).
 */
export default function ProgramMultiSelect(props: ProgramMultiSelectProps): JSX.Element {
  // Task 4.1 — lockedValues prop is now actively consumed for conditional editability.
  const lockedValues = props.lockedValues ?? [];

  /**
   * Parse the incoming comma-separated string into an array of trimmed tokens that are
   * present in PROGRAM_OPTIONS. Non-predefined tokens (e.g. legacy custom values) are
   * silently dropped to enforce the predefined-only requirement.
   */
  const parseValue = (csv: string): string[] =>
    csv
      .split(',')
      .map((v) => v.trim())
      .filter((v) => PROGRAM_OPTIONS.includes(v));

  const [selectedValues, setSelectedValues] = useState<string[]>(() => parseValue(props.value));
  const [query, setQuery] = useState<string>('');

  // Sync internal state when the Redux value changes externally (e.g. metadata load).
  useEffect(() => {
    setSelectedValues(parseValue(props.value));
  }, [props.value]);

  /**
   * Emit the updated selection back as a comma-separated string.
   * Deduplicates and trims tokens before joining.
   */
  const emitChange = (newValues: string[]): void => {
    const deduplicated = Array.from(new Set(newValues.map((v) => v.trim()).filter((v) => v.length > 0)));
    props.onChange(deduplicated.join(', '));
    setSelectedValues(deduplicated);
  };

  /**
   * Remove a specific value from the current selection.
   * Task 4.3 — locked values (from the mapping) cannot be removed.
   */
  const removeValue = (valueToRemove: string): void => {
    if (lockedValues.includes(valueToRemove)) {
      // Locked programs are auto-derived from Use Case selections and are not user-removable.
      return;
    }
    emitChange(selectedValues.filter((v) => v !== valueToRemove));
  };

  /**
   * Called by Combobox when an option is selected or deselected.
   *
   * With `multiple`, Headless UI passes the full new array of selected values.
   * Task 4.3 — prevent de-selection of locked values via the dropdown as well.
   */
  const handleChange = (newSelection: string[]): void => {
    // Locked values must remain selected regardless of user interaction.
    const withLocked = Array.from(new Set([...lockedValues, ...newSelection]));
    emitChange(withLocked);
    setQuery('');
  };

  // Build the filtered predefined options list based on the current query.
  const filteredOptions =
    query === ''
      ? PROGRAM_OPTIONS
      : PROGRAM_OPTIONS.filter((option) => option.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="w-full" data-testid="program-multi-select">
      <Combobox value={selectedValues} onChange={handleChange} multiple>
        <Label className="text-sm font-medium leading-6 text-gray-900 flex items-center">Program*</Label>

        {/* Trigger button — input for typing search queries */}
        <div className="relative mt-2 w-full cursor-default rounded-md bg-white text-left shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-gray-200 sm:text-sm sm:leading-6">
          <ComboboxInput
            className="w-full py-1.5 pl-1.5 pr-10 text-black bg-transparent border-none text-sm focus:outline-none rounded-md"
            onChange={(event) => setQuery(event.target.value)}
            displayValue={() => query}
            placeholder={selectedValues.length === 0 ? 'Select programs…' : 'Add more…'}
            aria-label="Program"
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2 cursor-pointer">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </ComboboxButton>
        </div>

        {/* Selected value chips shown below the input */}
        {selectedValues.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1" data-testid="selected-chips">
            {selectedValues.map((v) => {
              const isLocked = lockedValues.includes(v);
              return isLocked ? (
                /* Task 4.2 — locked chip: visually distinct (blue tint + lock icon), no remove button */
                <span
                  key={v}
                  data-testid={`locked-chip-${v}`}
                  className="inline-flex items-center gap-0.5 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200"
                  title="Auto-filled from Use Case mapping — not editable"
                >
                  <LockClosedIcon className="h-2.5 w-2.5 mr-0.5" aria-hidden="true" />
                  {v}
                </span>
              ) : (
                /* Unlocked chip: normal style with remove button (task 4.3) */
                <span
                  key={v}
                  className="inline-flex items-center gap-0.5 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200"
                >
                  {v}
                  <button
                    type="button"
                    aria-label={`Remove ${v}`}
                    onClick={() => removeValue(v)}
                    className="ml-0.5 rounded-full text-gray-400 hover:text-gray-600 focus:outline-none"
                  >
                    <XMarkIcon className="h-3 w-3" aria-hidden="true" />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {/* Dropdown options */}
        <ComboboxOptions
          anchor="bottom"
          transition
          className={classNames(
            'z-50 mt-1 w-[var(--input-width)] max-h-60 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm',
            'transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0',
          )}
        >
          {/* Predefined options */}
          {filteredOptions.map((option) => {
            const isLocked = lockedValues.includes(option);
            return (
              <ComboboxOption
                key={option}
                value={option}
                disabled={isLocked}
                className={classNames(
                  'group relative select-none py-1 pl-3 pr-9',
                  isLocked ? 'cursor-not-allowed opacity-60' : 'cursor-default data-[focus]:bg-gray-50',
                )}
              >
                {({ selected }) => (
                  <>
                    <span
                      className={classNames(
                        selected ? 'font-semibold text-gray-900' : 'text-gray-700',
                        'block truncate text-sm',
                      )}
                    >
                      {option}
                      {isLocked && <span className="ml-1 text-blue-500 text-xs">(auto-filled)</span>}
                    </span>
                    {selected && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600">
                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                      </span>
                    )}
                  </>
                )}
              </ComboboxOption>
            );
          })}

          {/* Empty state */}
          {filteredOptions.length === 0 && (
            <div className="relative cursor-default select-none py-2 px-4 text-sm text-gray-500">
              No options match your search.
            </div>
          )}
        </ComboboxOptions>
      </Combobox>
    </div>
  );
}
